import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { FormState } from 'ia-react-core/~/interfaces/FormState';
import withSchema from 'ia-react-core/~/hocs/schema/withSchema';

import compose from '../../../utils/components/_compose';
import withUniqueId from '../../../hocs/helpers/_withUniqueId';
import injectAnalyticsScope from '../../../hocs/analytics/injectAnalyticsScope';
import withValidators from '../../../hocs/form/_withValidators';
import injectReducer from '../../../hocs/redux/injectReducer';
import injectSaga from '../../../hocs/redux/injectSaga';
import withScope from '../../../hocs/redux/withScope';
import withTransclusion from '../../../hocs/transclusion/withTransclusion';
import getValuesDst from '../../../utils/components/_getValuesDst';
import updateFormById from '../../../actions/form-fn/updateFormById';
import submitFormById from '../../../actions/form-fn/submitFormById';
import withValidationContext from '../../../hocs/form/_withValidationContext';

import formActions from './Form.actions';
import formSelectors from './Form.selectors';

import withFormMethods from './withFormMethods';
import withFormContext from './withFormContext';
import withFormReducerKey from './withFormReducerKey';
import withInitialValues from './withInitialValues';
import Form, { FormComposedProps, FormProps } from './Form';
import formReducer from './Form.reducer';
import formValuesReducer from './Form.valuesReducer';
import formSaga from './Form.saga';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchUpdateFormById: (...args: Parameters<typeof updateFormById>) => dispatch(updateFormById(...args)),
  dispatchSubmitFormById: (...args: Parameters<typeof submitFormById>) => dispatch(submitFormById(...args)),
});

const ComposedForm: (<Values extends object>(
  props: FormProps<Values>
) => React.ReactElement<FormProps<Values>>) & {
  displayName: typeof Form.displayName;
  scopeProps: typeof Form.scopeProps;
  publicProps: typeof Form.publicProps;
  knownProps: typeof Form.knownProps;
} = compose<any>(
  withUniqueId('formId', { acceptOverride: true }),
  withFormContext,
  withSchema('form', { include: true, skip: ({ ancestorFormId, isolate }: FormComposedProps<{}>) => Boolean(ancestorFormId && !isolate) }),
  withScope,
  withInitialValues,
  withTransclusion,
  injectAnalyticsScope<FormComposedProps<{}>>({
    key: ({ name }) => name,
  }),
  withValidationContext,
  withValidators,
  withFormMethods,
  withFormReducerKey,
  injectReducer<FormComposedProps<{}>, FormState>({
    key: ({ formReducerKey }) => formReducerKey,
    idName: 'formId',
    skip: ({ ancestorFormId }) => Boolean(ancestorFormId),
    namespace: 'Core/Form',
    identifiers: ({ formId, name }) => ({ formId, name }),
    reducer: formReducer,
    actions: formActions,
    selectors: formSelectors,
    initialState: (props) => {
      const { formId, name } = props;

      return ({
        formId,
        name,
        version: 0,
        errors: {},
        warnings: {},
        cautions: {},
        clientErrors: {},
        touched: {},
        dirty: {},
        isInitialized: false,
        isSubmitting: false,
        isSubmitted: false,
        isValidating: false,
        valuesDst: getValuesDst(props),
      } as FormState);
    },
    useScope: true,
    createScope: false,
    cleanState: true,
  }),
  injectReducer<FormComposedProps<object>, object>({
    namespace: 'Core/Form',
    identifiers: ({ formId, name }) => ({ formId, name }),
    key: (props) => {
      const { ancestorValuesDst, parentFormScope, name } = props;

      if (ancestorValuesDst) {
        const valuePath = parentFormScope.slice(1);
        return `${[ancestorValuesDst, ...valuePath, name].join('.').replace(/\.\[/g, '[')}`;
      }

      return getValuesDst(props);
    },
    useScope: true,
    createScope: false,
    reducer: formValuesReducer,
    cleanState: ({ cleanValues }) => Boolean(cleanValues),
  }),
  connect(null, mapDispatchToProps),
  injectSaga<FormComposedProps<{}>>({
    key: ({ formId }) => `Form${formId}`,
    saga: formSaga,
  }),
)(Form);

ComposedForm.displayName = Form.displayName;

export * from './Form';
export default ComposedForm;
